import type { SearchKeyValueParam } from '../../types';
import {
  DEAKIN_DATE_FACET_ID,
  facetValueToSlug,
} from '../../funnelback/deakin-config';
import { facetIdsEqual } from './facet-id';
import {
  SEARCH_URL_FACET_KEY_PREFIX,
} from './search-url';

const DATE_FACET_PARAM_KEY = `${SEARCH_URL_FACET_KEY_PREFIX}${DEAKIN_DATE_FACET_ID}`;

/** Remembered encodings from select toggleUrls (unselect links omit `f.Date|d`). */
const dateFacetValueEncodingCache = new Map<string, string>();

/** Remembered encodings for all facets keyed by `${facetId}:${data}`. */
const facetValueEncodingCache = new Map<string, string>();

function facetValueCacheKey(facetId: string, data: string): string {
  return `${facetId}:${data}`;
}

function rememberFacetEncoding(
  facetId: string,
  data: string,
  param: SearchKeyValueParam,
): void {
  facetValueEncodingCache.set(facetValueCacheKey(facetId, data), param.value);
  if (facetId === DEAKIN_DATE_FACET_ID) {
    dateFacetValueEncodingCache.set(data, param.value);
  }
}

function getCachedFacetEncoding(
  facetId: string,
  data: string,
): SearchKeyValueParam | null {
  const cached = facetValueEncodingCache.get(facetValueCacheKey(facetId, data));
  if (!cached) {
    return null;
  }
  return { key: `${SEARCH_URL_FACET_KEY_PREFIX}${facetId}`, value: cached };
}

/** @internal Test helper */
export function clearDateFacetValueEncodingCache(): void {
  dateFacetValueEncodingCache.clear();
  facetValueEncodingCache.clear();
}

function rememberDateFacetEncoding(data: string, toggleUrl: string): void {
  for (const param of toggleUrlToFacetLookupExtras(toggleUrl)) {
    if (param.key === DATE_FACET_PARAM_KEY) {
      rememberFacetEncoding(DEAKIN_DATE_FACET_ID, data, param);
    }
  }
}

function yearBucketDateFacetValue(data: string): string | null {
  if (/^\d{4}$/.test(data)) {
    return `d=${data} :: ${data}`;
  }
  return null;
}

/**
 * Resolve the Funnelback-encoded value for a Date facet bucket.
 * Select toggleUrls carry `f.Date|d`; once selected, only unselect URLs remain.
 */
export function resolveDateFacetLookupParam(
  funnelbackFacets: unknown[],
  data: string,
): SearchKeyValueParam | null {
  const trimmedData = data.trim();
  if (!trimmedData) {
    return null;
  }

  const toggleUrl = findFacetToggleUrl(
    funnelbackFacets,
    DEAKIN_DATE_FACET_ID,
    trimmedData,
  );
  if (toggleUrl) {
    rememberDateFacetEncoding(trimmedData, toggleUrl);
    const param = toggleUrlToFacetLookupExtras(toggleUrl).find(
      (p) => p.key === DATE_FACET_PARAM_KEY,
    );
    if (param) {
      return param;
    }
  }

  const cached = dateFacetValueEncodingCache.get(trimmedData);
  if (cached) {
    return { key: DATE_FACET_PARAM_KEY, value: cached };
  }

  const yearValue = yearBucketDateFacetValue(trimmedData);
  if (yearValue) {
    return { key: DATE_FACET_PARAM_KEY, value: yearValue };
  }

  return null;
}

type UnknownRecord = Record<string, unknown>;

function asRecord(v: unknown): UnknownRecord | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as UnknownRecord)
    : null;
}

function asString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function decodeToggleQueryComponent(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value.replace(/\+/g, ' ');
  }
}

/** Split toggleUrl query without URLSearchParams (preserves repeated keys). */
function parseToggleUrlQuerySegments(toggleUrl: string): string[] {
  const trimmed = toggleUrl.trim();
  if (!trimmed) {
    return [];
  }
  const qIdx = trimmed.indexOf('?');
  const qs = qIdx >= 0 ? trimmed.slice(qIdx + 1) : trimmed;
  return qs ? qs.split('&').filter(Boolean) : [];
}

function parseQuerySegment(segment: string): { key: string; value: string } | null {
  const eqIdx = segment.indexOf('=');
  if (eqIdx < 0) {
    return null;
  }
  const key = segment.slice(0, eqIdx);
  const value = segment.slice(eqIdx + 1);
  if (!key) {
    return null;
  }
  return { key, value };
}

/**
 * Parse query string from a Funnelback `toggleUrl` (`?`-prefixed or bare).
 * Note: collapses duplicate keys — prefer manual segment parsers for facets.
 */
export function parseToggleUrlParams(toggleUrl: string): URLSearchParams {
  const trimmed = toggleUrl.trim();
  if (!trimmed) {
    return new URLSearchParams();
  }
  const qIdx = trimmed.indexOf('?');
  const qs = qIdx >= 0 ? trimmed.slice(qIdx + 1) : trimmed;
  return new URLSearchParams(qs);
}

/** All `f.<facetId>` keys from a Funnelback `toggleUrl` (order preserved). */
export function parseFacetIdsFromToggleUrl(toggleUrl: string): string[] {
  const ids: string[] = [];
  for (const segment of parseToggleUrlQuerySegments(toggleUrl)) {
    const pair = parseQuerySegment(segment);
    if (!pair) {
      continue;
    }
    const key = decodeToggleQueryComponent(pair.key);
    if (key.startsWith(SEARCH_URL_FACET_KEY_PREFIX)) {
      ids.push(key.slice(SEARCH_URL_FACET_KEY_PREFIX.length));
    }
  }
  return ids;
}

/**
 * Extract `f.*` params from a toggleUrl for Funnelback lookup requests.
 * Preserves repeated facet keys (URLSearchParams would collapse them).
 */
export function toggleUrlToFacetLookupExtras(
  toggleUrl: string,
): SearchKeyValueParam[] {
  const out: SearchKeyValueParam[] = [];
  for (const segment of parseToggleUrlQuerySegments(toggleUrl)) {
    const pair = parseQuerySegment(segment);
    if (!pair?.value) {
      continue;
    }
    const key = decodeToggleQueryComponent(pair.key);
    const value = decodeToggleQueryComponent(pair.value);
    if (key.startsWith(SEARCH_URL_FACET_KEY_PREFIX) && value) {
      out.push({ key, value });
    }
  }
  return out;
}

function facetValueMatchesToggleParam(data: string, paramValue: string): boolean {
  const label = data.trim();
  const decoded = decodeToggleQueryComponent(paramValue).trim();
  if (!label || !decoded) {
    return false;
  }
  if (label.toLowerCase() === decoded.toLowerCase()) {
    return true;
  }
  return facetValueToSlug(label) === facetValueToSlug(decoded);
}

function facetAllValues(facet: unknown): UnknownRecord[] {
  const f = asRecord(facet);
  if (!f) {
    return [];
  }
  const raw = f.allValues ?? f.values ?? f.categories;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.map(asRecord).filter((v): v is UnknownRecord => v !== null);
}

/**
 * Find a Funnelback facet value `toggleUrl` for the given facet id and data label.
 * Only returns select-style URLs that include the target facet param.
 */
export function findFacetToggleUrl(
  funnelbackFacets: unknown[],
  facetId: string,
  data: string,
): string | null {
  if (!Array.isArray(funnelbackFacets) || !facetId || !data) {
    return null;
  }
  for (const facet of funnelbackFacets) {
    for (const value of facetAllValues(facet)) {
      const valueData = asString(value.data ?? value.label);
      if (valueData !== data) {
        continue;
      }
      const toggleUrl = asString(value.toggleUrl);
      if (!toggleUrl) {
        continue;
      }
      const facetIds = parseFacetIdsFromToggleUrl(toggleUrl);
      if (facetIds.some((id) => facetIdsEqual(id, facetId))) {
        return toggleUrl;
      }
    }
  }
  return null;
}

function facetParamFromToggleUrl(
  toggleUrl: string,
  facetId: string,
  data?: string,
): SearchKeyValueParam | null {
  const matches = toggleUrlToFacetLookupExtras(toggleUrl).filter((p) =>
    facetIdsEqual(
      p.key.slice(SEARCH_URL_FACET_KEY_PREFIX.length),
      facetId,
    ),
  );
  if (matches.length === 0) {
    return null;
  }
  const trimmedData = data?.trim();
  if (trimmedData) {
    const byData = matches.find((p) =>
      facetValueMatchesToggleParam(trimmedData, p.value),
    );
    if (byData) {
      return byData;
    }
  }
  return matches[matches.length - 1];
}

function appendFacetParam(
  out: SearchKeyValueParam[],
  param: SearchKeyValueParam,
): void {
  out.push(param);
}

function appendFacetParamsFromData(
  out: SearchKeyValueParam[],
  facetId: string,
  data: string,
): void {
  const key = `${SEARCH_URL_FACET_KEY_PREFIX}${facetId}`;
  out.push({ key, value: data });
}

function dedupeFacetParams(
  params: SearchKeyValueParam[],
): SearchKeyValueParam[] {
  const seen = new Set<string>();
  const out: SearchKeyValueParam[] = [];
  for (const param of params) {
    const dedupeKey = `${param.key}\0${param.value}`;
    if (seen.has(dedupeKey)) {
      continue;
    }
    seen.add(dedupeKey);
    out.push(param);
  }
  return out;
}

export type BuildFunnelbackFacetParamsInput = {
  selectedFacets: Record<string, string[]>;
  funnelbackFacets: unknown[];
};

/**
 * Build Funnelback lookup extras for facets.
 * publishedDate drill-down uses `toggleUrl` encoding from the latest response.
 */
export function buildFunnelbackFacetParams(
  input: BuildFunnelbackFacetParamsInput,
): SearchKeyValueParam[] {
  const { selectedFacets, funnelbackFacets } = input;
  const out: SearchKeyValueParam[] = [];

  for (const [facetId, values] of Object.entries(selectedFacets)) {
    if (!values?.length) {
      continue;
    }
    for (const data of values) {
      if (typeof data !== 'string' || !data.trim()) {
        continue;
      }
      const trimmedData = data.trim();
      if (facetId === DEAKIN_DATE_FACET_ID) {
        const dateParam = resolveDateFacetLookupParam(
          funnelbackFacets,
          trimmedData,
        );
        if (dateParam) {
          appendFacetParam(out, dateParam);
          continue;
        }
      }
      const toggleUrl = findFacetToggleUrl(
        funnelbackFacets,
        facetId,
        trimmedData,
      );
      if (toggleUrl) {
        const param = facetParamFromToggleUrl(toggleUrl, facetId, trimmedData);
        if (param) {
          rememberFacetEncoding(facetId, trimmedData, param);
          appendFacetParam(out, param);
        } else {
          appendFacetParamsFromData(out, facetId, trimmedData);
        }
      } else {
        const cached = getCachedFacetEncoding(facetId, trimmedData);
        if (cached) {
          appendFacetParam(out, cached);
        } else {
          appendFacetParamsFromData(out, facetId, trimmedData);
        }
      }
    }
  }

  return dedupeFacetParams(out);
}

/** Normalized facet state for stable fetch-effect cache keys. */
export function normalizeSelectedFacetsForRequestKey(
  selectedFacets: Record<string, string[]>,
): Array<[string, string[]]> {
  return Object.entries(selectedFacets)
    .filter(([, values]) => values?.length > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([facetId, values]) => [
      facetId,
      [...values].map((v) => v.trim()).filter(Boolean).sort(),
    ]);
}

/**
 * Stable key for facet selection so fetch effects re-run when chips change.
 */
export function funnelbackFacetParamsCacheKey(
  selectedFacets: Record<string, string[]>,
): string {
  return JSON.stringify({
    facets: normalizeSelectedFacetsForRequestKey(selectedFacets),
  });
}
